import React, { useMemo } from "react";
import {
  Box,
  Container,
  Grid,
  Typography,
  Divider,
  Link,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Paper,
} from "@mui/material";
import { useSEO, useJsonLD } from "./SEO";

// ===== Theme tokens (aligned with your Fees/Privacy pages) =====
const ACCENT = "#F0B90B"; // gold
const PAGE_BG = "#0B0E11"; // page background
const BG = "#263238"; // dark surface for content
const SURFACE = "rgba(38,50,56,0.85)"; // glassy card
const BORDER = "rgba(255,255,255,0.08)";
const TEXT = "#EAEFF2";
const MUTED = "#B0BEC5";

function Section({ id, title, children }) {
  return (
    <Box id={id} sx={{ scrollMarginTop: { xs: 88, md: 110 }, mb: 5 }}>
      <Typography
        variant="h5"
        sx={{ fontWeight: 700, letterSpacing: 0.3, color: ACCENT, mb: 1.5 }}
      >
        {title}
      </Typography>
      <Box
        sx={{
          color: MUTED,
          "& p": { mb: 1.5, lineHeight: 1.75 },
          "& ul": { pl: 3, mb: 1.5 },
          "& li": { mb: 0.75 },
          "& a": { color: ACCENT, fontWeight: 700 },
        }}
      >
        {children}
      </Box>
      <Divider sx={{ mt: 3, borderColor: BORDER }} />
    </Box>
  );
}

export default function Terms({
  brandName = "1CryptoX",
  legalEntity = "1CryptoX Global Ltd.",
  contactEmail = "support@1cryptox.com",
  canonical = "https://www.1cryptox.com/terms",
  effectiveDateISO = new Date().toISOString(),
}) {
  const effectiveDate = useMemo(() => {
    try {
      const d = new Date(effectiveDateISO);
      return d.toLocaleDateString(undefined, {
        year: "numeric",
        month: "long",
        day: "2-digit",
      });
    } catch {
      return effectiveDateISO;
    }
  }, [effectiveDateISO]);

  // ---------- SEO ----------
  const title = `Terms of Use • ${brandName}`;
  const description = `${brandName} Terms of Use — professional, comprehensive rules governing eligibility, compliance, trading conduct (Spot/Futures/AI), risk disclosures, fees/taxes, IP, prohibited activities, liability, dispute resolution, and more.`;
  const keywords = [
    "Terms of Use",
    "User Agreement",
    "Digital Assets",
    "Trading Rules",
    "Risk Disclosure",
    "KYC",
    "AML",
    brandName,
  ];

  useSEO({
    title,
    description,
    keywords,
    canonical,
    robots: "index,follow",
    og: {
      type: "website",
      title,
      description,
      url: canonical,
      image: "https://www.1cryptox.com/og/og-terms.jpg",
      siteName: brandName,
    },
    twitter: {
      card: "summary_large_image",
      title,
      description,
      image: "https://www.1cryptox.com/og/og-terms.jpg",
    },
  });

  // ---------- JSON-LD ----------
  useJsonLD("org", {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: legalEntity || brandName,
    url: canonical.replace("/terms", ""),
    logo: "https://www.1cryptox.com/logo.png",
  });

  useJsonLD("webpage", {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: title,
    description,
    url: canonical,
    dateModified: effectiveDateISO,
  });

  useJsonLD("breadcrumb", {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Home",
        item: canonical.replace("/terms", "/"),
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Terms of Use",
        item: canonical,
      },
    ],
  });

  // Sidebar items (expanded & enterprise-grade)
  const items = [
    { id: "intro", label: "1. Introduction" },
    { id: "definitions", label: "2. Definitions" },
    { id: "acceptance", label: "3. Acceptance of Terms" },
    { id: "eligibility", label: "4. Eligibility & Account" },
    {
      id: "compliance",
      label: "5. Compliance (KYC/AML/Sanctions/Export Controls)",
    },
    { id: "risk", label: "6. Risk Disclosure & No Advice" },
    { id: "trading", label: "7. Trading Conduct (Spot/Futures/AI)" },
    { id: "orders", label: "8. Orders, Finality & Errors" },
    { id: "fees", label: "9. Fees, Funding, Taxes" },
    {
      id: "wallets",
      label: "10. Deposits, Withdrawals & Restricted Jurisdictions",
    },
    { id: "security", label: "11. Account Security & Notifications" },
    { id: "prohibited", label: "12. Prohibited Activities" },
    { id: "thirdparty", label: "13. Third-Party Services" },
    { id: "ip", label: "14. Intellectual Property" },
    { id: "warranties", label: "15. No Warranties" },
    { id: "liability", label: "16. Limitation of Liability" },
    { id: "indemnity", label: "17. Indemnification" },
    { id: "force", label: "18. Force Majeure" },
    { id: "termination", label: "19. Suspension & Termination" },
    {
      id: "law",
      label: "20. Governing Law, Arbitration & Class Action Waiver",
    },
    {
      id: "misc",
      label: "21. Miscellaneous (Severability, Assignment, Notices…)",
    },
    { id: "changes", label: "22. Amendments" },
    { id: "contact", label: "23. Contact" },
  ];

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background:
          "radial-gradient(60% 60% at 70% 30%, rgba(240,185,11,0.10) 0%, rgba(240,185,11,0.03) 100%)",
        bgcolor: "#0B0E11",
      }}
    >
      {/* Hero */}
      <Box
        sx={{
          position: "relative",
          overflow: "hidden",
          borderBottom: "1px solid",
          borderColor: BORDER,
        }}
      >
        <Container sx={{ py: { xs: 6, md: 8 } }}>
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 1,
              px: 1.5,
              py: 0.5,
              borderRadius: 999,
              border: `1px solid rgba(240,185,11,0.35)`,
              bgcolor: "rgba(240,185,11,0.08)",
              color: ACCENT,
              fontSize: 12,
              letterSpacing: 0.6,
              fontWeight: 700,
            }}
          >
            LEGAL & COMPLIANCE
          </Box>

          <Typography
            variant="h3"
            sx={{ mt: 2, fontWeight: 800, letterSpacing: 0.2, color: TEXT }}
          >
            Terms of Use
          </Typography>

          <Typography
            variant="body1"
            sx={{ mt: 1.5, maxWidth: 820, color: MUTED }}
          >
            These Terms of Use (“Terms”) constitute a binding agreement between
            you and {legalEntity || brandName}
            governing access to and use of our websites, mobile applications,
            and digital-asset services (the “Services”).
          </Typography>

          <Typography
            variant="caption"
            sx={{ mt: 1, display: "block", color: MUTED }}
          >
            Effective Date: {effectiveDate}
          </Typography>
        </Container>
      </Box>

      {/* Body */}
      <Container sx={{ py: { xs: 5, md: 8 } }}>
        <Grid container spacing={4}>
          {/* Sidebar */}
          <Grid item xs={12} md={4} lg={3} order={{ xs: 2, md: 1 }}>
            <Paper
              elevation={0}
              sx={{
                position: { md: "sticky" },
                top: { md: 96 },
                p: 2,
                borderRadius: 3,
                border: `1px solid ${BORDER}`,
                bgcolor: SURFACE,
                backdropFilter: "blur(6px)",
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{ mb: 1, color: MUTED, fontWeight: 700 }}
              >
                On this page
              </Typography>
              <List dense disablePadding>
                {items.map((it) => (
                  <ListItem key={it.id} disableGutters sx={{ mb: 0.25 }}>
                    <ListItemButton
                      component="a"
                      href={`#${it.id}`}
                      sx={{
                        borderRadius: 2,
                        px: 1.25,
                        py: 0.75,
                        color: TEXT,
                        "&:hover": { bgcolor: "rgba(240,185,11,0.12)" },
                      }}
                    >
                      <ListItemText
                        primaryTypographyProps={{ fontSize: 13 }}
                        primary={it.label}
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </Paper>
          </Grid>

          {/* Content */}
          <Grid item xs={12} md={8} lg={9} order={{ xs: 1, md: 2 }}>
            <Paper
              elevation={0}
              sx={{
                bgcolor: BG,
                border: `1px solid ${BORDER}`,
                borderRadius: 2,
                p: { xs: 2, md: 3 },
              }}
            >
              <Section id="intro" title="1. Introduction">
                <p>
                  These Terms govern your relationship with {brandName}.
                  Product-specific terms and policies may apply and are
                  incorporated by reference.
                </p>
              </Section>

              <Section id="definitions" title="2. Definitions">
                <p>
                  <strong>“Account”</strong> means your registered user profile
                  for accessing the Services.
                </p>
                <p>
                  <strong>“Digital Assets”</strong> mean cryptographic tokens,
                  coins, or digital currencies supported on the platform.
                </p>
                <p>
                  <strong>“You” or “User”</strong> means any individual or
                  entity accessing or using the Services.
                </p>
                <p>
                  <strong>“We”, “Us”, “Our”</strong> means{" "}
                  {legalEntity || brandName} and its affiliates.
                </p>
              </Section>

              <Section id="acceptance" title="3. Acceptance of Terms">
                <p>
                  By creating an Account or using the Services, you agree to
                  these Terms and any policies referenced herein. If you do not
                  agree, discontinue use immediately.
                </p>
              </Section>

              <Section id="eligibility" title="4. Eligibility & Account">
                <ul>
                  <li>
                    You must be at least 18 (or age of majority in your
                    jurisdiction) and legally capable of entering into
                    contracts.
                  </li>
                  <li>
                    You are responsible for providing accurate information and
                    safeguarding credentials.
                  </li>
                  <li>
                    We may restrict access based on region, legal constraints,
                    or risk considerations.
                  </li>
                </ul>
              </Section>

              <Section
                id="compliance"
                title="5. Compliance (KYC/AML/Sanctions/Export Controls)"
              >
                <p>
                  You agree to comply with applicable KYC/AML laws, sanctions
                  regimes, and export controls. We may request identity
                  verification, monitor transactions, and suspend access for
                  compliance purposes.
                </p>
              </Section>

              <Section id="risk" title="6. Risk Disclosure & No Advice">
                <p>
                  Digital-asset markets are highly volatile and can result in
                  substantial losses. You understand these risks, engage at your
                  own discretion, and acknowledge that {brandName} does not
                  provide investment, legal, or tax advice.
                </p>
              </Section>

              <Section
                id="trading"
                title="7. Trading Conduct (Spot/Futures/AI)"
              >
                <ul>
                  <li>
                    <strong>Spot:</strong> Orders execute based on liquidity;
                    slippage and partial fills may occur.
                  </li>
                  <li>
                    <strong>Futures:</strong> Leverage, margin, funding, and
                    liquidation rules apply; you must manage positions and risk.
                  </li>
                  <li>
                    <strong>AI:</strong> Automated strategies are tools only;
                    performance is not guaranteed and not investment advice.
                  </li>
                </ul>
              </Section>

              <Section id="orders" title="8. Orders, Finality & Errors">
                <p>
                  Confirmed or pending orders are generally final and may be
                  irreversible. We may cancel, reverse, or adjust orders in
                  cases of system error, market abuse, law enforcement request,
                  or exceptional circumstances.
                </p>
              </Section>

              <Section id="fees" title="9. Fees, Funding, Taxes">
                <p>
                  Fees, funding rates, and withdrawal charges are displayed
                  in-app and may change with notice. You are solely responsible
                  for any taxes applicable to your activities and for meeting
                  reporting obligations.
                </p>
              </Section>

              <Section
                id="wallets"
                title="10. Deposits, Withdrawals & Restricted Jurisdictions"
              >
                <ul>
                  <li>
                    Network confirmations, limits, and fees apply; on-chain
                    delays lie outside our control.
                  </li>
                  <li>
                    Always verify addresses and networks; blockchain
                    transactions are typically irreversible.
                  </li>
                  <li>
                    Access may be restricted in certain jurisdictions due to
                    regulatory requirements.
                  </li>
                </ul>
              </Section>

              <Section
                id="security"
                title="11. Account Security & Notifications"
              >
                <p>
                  You must keep credentials confidential and enable available
                  security controls. Notify us immediately at{" "}
                  <Link
                    href={`mailto:${contactEmail}`}
                    underline="always"
                    sx={{ color: ACCENT, fontWeight: 700 }}
                  >
                    {contactEmail}
                  </Link>{" "}
                  of any suspected compromise.
                </p>
              </Section>

              <Section id="prohibited" title="12. Prohibited Activities">
                <ul>
                  <li>
                    Illegal activity, sanctions evasion, or attempts to
                    circumvent controls.
                  </li>
                  <li>
                    Market manipulation (e.g., wash trading, spoofing), abusive
                    or deceptive conduct.
                  </li>
                  <li>
                    Unauthorized scraping, reverse engineering, or interference
                    with the Services.
                  </li>
                </ul>
              </Section>

              <Section id="thirdparty" title="13. Third-Party Services">
                <p>
                  The Services may rely on third-party APIs, custodians, or data
                  sources. We are not responsible for third-party availability,
                  accuracy, or practices; use is at your discretion and subject
                  to their terms.
                </p>
              </Section>

              <Section id="ip" title="14. Intellectual Property">
                <p>
                  All content, trademarks, and software are owned or licensed by{" "}
                  {brandName}. Subject to these Terms, a limited, revocable,
                  non-transferable license is granted for lawful use of the
                  Services.
                </p>
              </Section>

              <Section id="warranties" title="15. No Warranties">
                <p>
                  The Services are provided “as is” and “as available”, without
                  warranties of any kind (express or implied), including
                  merchantability, fitness for a particular purpose, or
                  non-infringement.
                </p>
              </Section>

              <Section id="liability" title="16. Limitation of Liability">
                <p>
                  To the fullest extent permitted by law, {brandName} and
                  affiliates shall not be liable for indirect, incidental,
                  special, consequential, exemplary, or punitive damages, or
                  lost profits/data/goodwill arising from your use of the
                  Services.
                </p>
              </Section>

              <Section id="indemnity" title="17. Indemnification">
                <p>
                  You agree to indemnify and hold {brandName}, affiliates,
                  officers, employees, and partners harmless from claims,
                  damages, losses, liabilities, and expenses arising from your
                  use of the Services or breach of these Terms or law.
                </p>
              </Section>

              <Section id="force" title="18. Force Majeure">
                <p>
                  We are not liable for delays or failures caused by events
                  beyond our reasonable control, including network outages,
                  cyberattacks, market disruptions, acts of God, or regulatory
                  actions.
                </p>
              </Section>

              <Section id="termination" title="19. Suspension & Termination">
                <p>
                  We may suspend or terminate access at our discretion for
                  compliance, risk, or breach reasons. Upon termination, certain
                  provisions survive, including ownership, disclaimers,
                  liability limits, and dispute resolution.
                </p>
              </Section>

              <Section
                id="law"
                title="20. Governing Law, Arbitration & Class Action Waiver"
              >
                <p>
                  Unless otherwise required by local law, these Terms are
                  governed by applicable international principles and the laws
                  indicated in our regional notices (if any). Disputes may be
                  subject to binding arbitration on an individual basis, and you
                  waive participation in class actions where such waiver is
                  enforceable.
                </p>
              </Section>

              <Section
                id="misc"
                title="21. Miscellaneous (Severability, Assignment, Notices…)"
              >
                <ul>
                  <li>
                    <strong>Severability:</strong> If any provision is found
                    invalid, the remainder remains in effect.
                  </li>
                  <li>
                    <strong>Assignment:</strong> You may not assign these Terms
                    without our consent; we may assign to affiliates or in a
                    merger.
                  </li>
                  <li>
                    <strong>Notices:</strong> We may notify you via email,
                    in-app messages, or website postings.
                  </li>
                  <li>
                    <strong>Entire Agreement:</strong> These Terms and
                    referenced policies constitute the entire agreement.
                  </li>
                  <li>
                    <strong>Headings:</strong> Headings are for convenience only
                    and do not affect interpretation.
                  </li>
                </ul>
              </Section>

              <Section id="changes" title="22. Amendments">
                <p>
                  We may update these Terms from time to time. Changes become
                  effective upon posting. Your continued use of the Services
                  after changes take effect constitutes acceptance.
                </p>
              </Section>

              <Section id="contact" title="23. Contact">
                <p>
                  For questions about these Terms, contact{" "}
                  <Link
                    href={`mailto:${contactEmail}`}
                    underline="always"
                    sx={{ color: ACCENT, fontWeight: 700 }}
                  >
                    {contactEmail}
                  </Link>
                  .
                </p>
                <Typography variant="caption" sx={{ color: MUTED }}>
                  In the event of any conflict between these Terms and local or
                  regional notices, the latter may prevail to the extent
                  required by law.
                </Typography>
              </Section>
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}
